import { createAsyncThunk } from '@reduxjs/toolkit';
import apiFetch, { genericErrorMessage } from '@/services/apiFetch';
import { fetchPositions } from '@/services/permissions.endpoint';
import { getPalmettoGroups } from '@/services/accounts.endpoint';
import PalmettoGroupsJSON from '@/common/const/groups.json';
import type { RootState } from '@/store';
import { putDashboardSettings } from '@/services/settings.endpoint';
import { sendSupportMsg } from '@/services/notificationsEndpoint';

export const groupAWS = createAsyncThunk<PalmettoGroups[], void>('PALMETTO_GROUP_AWS', async () => {
  try {
    const endpoint = getPalmettoGroups();
    return await apiFetch<PalmettoGroups[]>(endpoint)
      .then((res) => res?.data || null)
      .catch(() => {
        console.log('Error fetching groups and use local data');
        return PalmettoGroupsJSON as PalmettoGroups[];
      });
  } catch (error) {
    let message = genericErrorMessage;
    if (error instanceof Error) message = error.message;
    else {
      if (typeof error === 'object' && error !== null) {
        if ('message' in error) {
          message = String(error.message);
        }
      }
    }
    throw Error(message);
  }
});

export const updateDashboardSettings = createAsyncThunk(
  'ACCOUNT/UPDATE_DASHBOARD_SETTINGS',
  async (newSettings: Nullable<DashboardSettingsUpdate>, thunkAPI) => {
    try {
      const store = thunkAPI.getState() as RootState;
      if (!store.account.hasFetchedDashboardSettings) return;
      const userSettings = store.account.dashboardSettings;
      const userId = store.auth.userId;
      const selectedGroup = store.auth.tokenVerification.pvActiveGroupID;
      if (!userId) {
        return thunkAPI.rejectWithValue('User ID not found');
      }

      // Parse existing settings if `pvSettingValue` exists, otherwise start with an empty object
      let existingSettings: DashboardSettingsUpdate = {};
      if (userSettings?.pvSettingValue) {
        try {
          existingSettings = JSON.parse(userSettings.pvSettingValue);
        } catch (error) {
          console.warn('Error parsing existing pvSettingValue:', error);
          existingSettings = {};
        }
      }
      // Merge existing settings with new settings (handling the case where `userSettings` is null)
      let updatedSettings: DashboardSettingsUpdate = {};
      if (newSettings) {
        updatedSettings = {
          ...existingSettings,
          ...newSettings,
        };
      }
      const payload: Partial<ISettings> = {
        pvSettingID: userSettings?.pvSettingID ?? null, // Preserve `pvSettingID` if it exists
        pvAccountID: Number(userId),
        pvSettingType: 'dashboard3Settings',
        pvGroupID: selectedGroup,
        pvSettingValue: newSettings
          ? JSON.stringify(updatedSettings)
          : JSON.stringify(existingSettings),
      };

      const endpoint = putDashboardSettings(payload);
      return await apiFetch<ISettings>(endpoint).then((res) => res.data ?? null);
    } catch (error) {
      let message = genericErrorMessage;
      if (error instanceof Error) message = error.message;
      else if (typeof error === 'object' && error !== null && 'message' in error) {
        message = String(error.message);
      }
      throw Error(message);
    }
  }
);

interface PermissionsResponse {
  dataset: IPermissionsInterface[];
  count: number;
}

export const getPermissions = createAsyncThunk('ACCOUNT/FETCH_PERMISSIONS', async (_, { rejectWithValue, getState }) => {
  try {
    const store = getState() as RootState;
    const positions = store.account.account?.currentPositions || [];
    if (!positions || positions.length === 0) {
      return rejectWithValue('No positions found');
    }
    const positionsArray = positions?.map((position) => position.i);
    const encodePositions = JSON.stringify(positionsArray);
    const endpoint = fetchPositions(encodePositions);
    return await apiFetch<PermissionsResponse>(endpoint).then((res) => res.data.dataset ?? null);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
  }
});

export const postSupportMsg = createAsyncThunk(
  'send-support-msg',
  async (data: ContactFormData) => {
    try {
      const endpoint = sendSupportMsg(data);
      return await apiFetch<string>(endpoint).then((res) => res?.data || null);
    } catch (error) {
      let message: string;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      console.log('send-support-msg catch:', error);
      throw Error(message);
    }
  }
);
